/**
 * ChatLayout — Main chat interface with sidebar, message area,
 * and input. Orchestrates the agent stream and renders messages
 * with inline visual components.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useUser, UserButton } from "@clerk/react";
import { useDeal } from "@/context/DealContext";
import { useAgentStream } from "@/hooks/useAgentStream";
import type { DealCallbacks } from "@/hooks/useAgentStream";
import { ComponentRenderer } from "@/components/shared/ComponentRenderer";
import DealHeaderCard from "@/components/cards/DealHeaderCard";
import GuidedChips from "@/components/chat/GuidedChips";
import DealSidebar from "@/components/chat/DealSidebar";
import DealDetailPanel from "@/components/deal/DealDetailPanel";
import { BillingStatusWidget } from "@/components/billing/BillingStatus";
import { PlanSelector } from "@/components/billing/PlanSelector";
import { SettingsPanel } from "@/components/settings/SettingsPanel";
import * as api from "@/api/client";
import type { DealData } from "@/api/client";
import type { Conversation, Message } from "@/api/types";

// ── Simple Markdown Renderer ─────────────────────────────────
// Minimal renderer matching the original chat.js approach.
// Replace with react-markdown for production.

function renderMarkdown(text: string): string {
  if (!text) return "";
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Code blocks
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, _lang, code) =>
    `<pre><code>${code.trim()}</code></pre>`
  );
  // Inline code
  html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  // Headers
  html = html.replace(/^### (.+)$/gm, "<h4>$1</h4>");
  html = html.replace(/^## (.+)$/gm, "<h3>$1</h3>");
  // Lists
  html = html.replace(/^- (.+)$/gm, "<li>$1</li>");
  html = html.replace(/(<li>.*<\/li>\n?)+/g, "<ul>$&</ul>");
  // Tables
  html = html.replace(/^\|(.+)\|$/gm, (match) => {
    const cells = match.split("|").filter((c) => c.trim());
    if (cells.every((c) => /^[\s-:]+$/.test(c))) return "";
    return "<tr>" + cells.map((c) => `<td>${c.trim()}</td>`).join("") + "</tr>";
  });
  html = html.replace(/(<tr>.*<\/tr>\n?)+/g, "<table>$&</table>");
  // Paragraphs
  html = html.replace(/^(?!<[a-z])((?!$).+)$/gm, "<p>$1</p>");
  html = html.replace(/<p>\s*<\/p>/g, "");

  return html;
}

// ── Tool Name Formatter ──────────────────────────────────────

function formatToolName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── SVG Icons ────────────────────────────────────────────────

function SendIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function BrandIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function WelcomeBrandIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

// ── ChatLayout Component ─────────────────────────────────────

export function ChatLayout() {
  const { user: clerkUser } = useUser();
  const user = clerkUser ? { id: clerkUser.id, email: clerkUser.primaryEmailAddress?.emailAddress ?? "", display_name: clerkUser.fullName ?? undefined } : null;
  const {
    deals,
    activeDeal,
    activeDSR,
    chips,
    setActiveDeal,
    setActiveDSR,
    createDeal,
    updateDeal,
    handleDealContext,
    handleDSRUpdate,
    handleChipsUpdate,
  } = useDeal();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConvo, setCurrentConvo] = useState<Conversation | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [detailDealId, setDetailDealId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Bridge SSE deal events to DealContext
  const dealCallbacks = useMemo<DealCallbacks>(
    () => ({
      onDealContext: handleDealContext,
      onDSRUpdate: handleDSRUpdate,
      onChipsUpdate: handleChipsUpdate,
    }),
    [handleDealContext, handleDSRUpdate, handleChipsUpdate]
  );

  const {
    messages,
    isStreaming,
    toolIndicators,
    usage,
    error,
    send,
    loadMessages,
    clear,
  } = useAgentStream(currentConvo?.id ?? null, dealCallbacks);

  // Refresh billing widget whenever a usage event arrives from the SSE stream
  const [billingRefreshKey, setBillingRefreshKey] = useState(0);
  useEffect(() => {
    if (usage) setBillingRefreshKey((k) => k + 1);
  }, [usage]);

  // Load conversations on mount
  useEffect(() => {
    api.listConversations().then(setConversations);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, toolIndicators]);

  // Open a conversation and restore deal context if linked
  const openConversation = useCallback(
    async (convo: Conversation) => {
      setCurrentConvo(convo);
      setSidebarOpen(false);
      clear();

      // Restore deal context if conversation is linked to a deal
      if (convo.deal_id) {
        try {
          const [deal, dsr] = await Promise.all([
            api.getDeal(convo.deal_id),
            api.getDealDSR(convo.deal_id),
          ]);
          if (deal) setActiveDeal(deal);
          if (dsr) {
            handleDSRUpdate({
              deal_id: deal?.id || convo.deal_id,
              completeness_score: (dsr.completeness_score as number) || 0,
              pir_status: (dsr.pir_status as Record<string, string>) || {},
              ground_truth: (dsr.ground_truth as Record<string, unknown>) || {},
              estimates: (dsr.estimates as Record<string, unknown>) || {},
            });
          }
        } catch {
          // Non-critical — deal may have been deleted
        }
      } else {
        setActiveDeal(null);
        setActiveDSR(null);
      }

      let data: Awaited<ReturnType<typeof api.getConversation>> = null;
      try {
        data = await api.getConversation(convo.id);
      } catch {
        // Network error or auth timeout — show empty conversation rather than hanging
      }
      if (data?.messages) {
        // Build messages, attaching component data from tool results
        // to the preceding assistant message
        const msgs: Message[] = [];
        for (let i = 0; i < data.messages.length; i++) {
          const m = data.messages[i]!;
          if (m.role === "tool") {
            // Attach component to the last assistant message if present
            const comp = (m as Record<string, unknown>).component as
              | { tool: string; data: Record<string, unknown> }
              | undefined;
            if (comp && msgs.length > 0) {
              const last = msgs[msgs.length - 1]!;
              if (last.role === "assistant") {
                if (!last.components) last.components = [];
                last.components.push({
                  id: `hist_comp_${i}`,
                  tool: comp.tool,
                  data: comp.data,
                });
              }
            }
            continue; // Don't add tool messages as standalone entries
          }
          msgs.push({
            id: `hist_${i}`,
            role: m.role as "user" | "assistant",
            content: m.content,
          });
        }
        loadMessages(msgs);
      }
    },
    [clear, loadMessages, setActiveDeal, setActiveDSR, handleDSRUpdate]
  );

  // Create new conversation
  const newChat = useCallback(async () => {
    const convo = await api.createConversation();
    if (convo) {
      setCurrentConvo(convo);
      clear();
      setSidebarOpen(false);
      setActiveDeal(null);
      setActiveDSR(null);
      const updated = await api.listConversations();
      setConversations(updated);
    }
  }, [clear, setActiveDeal, setActiveDSR]);

  // Send message
  const handleSend = useCallback(async () => {
    const content = inputValue.trim();
    if (!content || isStreaming) return;

    // Auto-create conversation if needed, capturing the ID directly
    // (React state updates are async, so we can't rely on currentConvo after setCurrentConvo)
    let convoId = currentConvo?.id;
    if (!convoId) {
      const convo = await api.createConversation();
      if (!convo) return;
      convoId = convo.id;
      setCurrentConvo(convo);
      const updated = await api.listConversations();
      setConversations(updated);
    }

    setInputValue("");
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      // Blur on mobile to dismiss the soft keyboard after sending
      if (window.innerWidth <= 768) inputRef.current.blur();
    }
    await send(content, convoId);
  }, [inputValue, isStreaming, currentConvo, send]);

  // Follow-up from component interaction (e.g., clicking a comp sale or welcome example)
  const handleFollowUp = useCallback(
    (message: string) => {
      setInputValue(message);
      // Auto-send after a brief moment so the user sees what's happening
      setTimeout(async () => {
        if (isStreaming) return;

        // Auto-create conversation if needed (same pattern as handleSend)
        let convoId = currentConvo?.id;
        if (!convoId) {
          const convo = await api.createConversation();
          if (!convo) return;
          convoId = convo.id;
          setCurrentConvo(convo);
          const updated = await api.listConversations();
          setConversations(updated);
        }

        setInputValue("");
        await send(message, convoId);
      }, 200);
    },
    [isStreaming, currentConvo, send]
  );

  // Handle deal creation from sidebar
  const handleCreateDeal = useCallback(
    async (deal: DealData, message: string) => {
      // Add deal to context
      setActiveDeal(deal);

      // Create conversation linked to deal
      const convo = await api.createConversation(
        deal.address || "New Deal",
        deal.id,
      );
      if (!convo) return;

      setCurrentConvo(convo);
      clear();
      setSidebarOpen(false);
      const updated = await api.listConversations();
      setConversations(updated);

      // Auto-send the analysis message
      setInputValue("");
      await send(message, convo.id);
    },
    [clear, send, setActiveDeal]
  );

  // Handle "Continue Analysis" from welcome screen
  const handleContinueDeal = useCallback(
    async (deal: DealData) => {
      setActiveDeal(deal);

      // Load DSR
      try {
        const dsr = await api.getDealDSR(deal.id);
        if (dsr) {
          handleDSRUpdate({
            deal_id: deal.id,
            completeness_score: (dsr.completeness_score as number) || 0,
            pir_status: (dsr.pir_status as Record<string, string>) || {},
            ground_truth: (dsr.ground_truth as Record<string, unknown>) || {},
            estimates: (dsr.estimates as Record<string, unknown>) || {},
          });
        }
      } catch { /* non-critical */ }

      // Create new conversation linked to deal
      const convo = await api.createConversation(
        `Continue: ${deal.address || deal.title || "Deal"}`,
        deal.id,
      );
      if (!convo) return;

      setCurrentConvo(convo);
      clear();
      const updated = await api.listConversations();
      setConversations(updated);

      // Auto-send resume message
      const address = deal.address || deal.title || "this property";
      await send(`Continue analysis on ${address}`, convo.id);
    },
    [clear, send, setActiveDeal, handleDSRUpdate]
  );

  // Keyboard handling
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + "px";
  };

  // Active non-closed deals for welcome screen
  const activeDeals = useMemo(
    () => deals.filter((d) => !["closed_won", "closed_lost"].includes(d.status)).slice(0, 3),
    [deals]
  );

  return (
    <div className="chat-layout">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <span className="sidebar-brand-icon">
              <BrandIcon />
            </span>
            <span className="sidebar-brand-text">Hoosier Intel</span>
          </div>
        </div>

        <DealSidebar
          conversations={conversations}
          currentConvoId={currentConvo?.id ?? null}
          onOpenConversation={openConversation}
          onDeleteConversation={async (id) => {
            await api.deleteConversation(id);
            if (currentConvo?.id === id) {
              setCurrentConvo(null);
              clear();
            }
            setConversations(await api.listConversations());
          }}
          onNewChat={newChat}
          onCreateDeal={handleCreateDeal}
          onOpenDealDetail={(dealId) => setDetailDealId(dealId)}
        />

        <BillingStatusWidget
          refreshKey={billingRefreshKey}
          onUpgrade={() => setShowUpgradeModal(true)}
        />

        <div style={{ padding: "4px 12px", borderTop: "1px solid var(--border-primary, #2a2a2a)" }}>
          <button
            onClick={() => setSettingsOpen(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              width: "100%",
              padding: "6px 4px",
              background: "none",
              border: "none",
              color: "var(--text-secondary, #888)",
              cursor: "pointer",
              fontSize: 12,
              borderRadius: 4,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--text-primary, #fff)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary, #888)")}
          >
            <GearIcon /> Settings
          </button>
        </div>

        <div className="sidebar-footer">
          <span className="user-email">{user?.email}</span>
          <UserButton />
        </div>
      </aside>

      {/* Main area */}
      <main className="chat-main">
        {/* Header */}
        <div className="chat-header">
          <button
            className={`menu-btn ${sidebarOpen ? "open" : ""}`}
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          >
            <span className="menu-icon">
              <span className="menu-icon-bar" />
              <span className="menu-icon-bar" />
              <span className="menu-icon-bar" />
            </span>
          </button>

          <span className="chat-title">
            {currentConvo?.title || "Select or start a conversation"}
          </span>

          <div className="chat-header-right">
            {isStreaming && <span className="status-dot streaming" />}
          </div>
        </div>

        {/* Deal header (when a deal is active) */}
        {activeDeal && activeDSR && (
          <DealHeaderCard
            address={activeDeal.address}
            parcelId={activeDeal.parcel_id}
            stage={activeDeal.status}
            completeness={(activeDSR.completeness_score as number) ?? 0}
            title={activeDeal.title}
            onClick={() => setDetailDealId(activeDeal.id)}
          />
        )}

        {/* Messages */}
        <div className="messages-container">
          {messages.length === 0 && !currentConvo && (
            <WelcomeScreen
              onExample={handleFollowUp}
              activeDeals={activeDeals}
              onContinueDeal={handleContinueDeal}
            />
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.role}`}>
              <div className="avatar">
                {msg.role === "user" ? "You" : "HI"}
              </div>
              <div className="message-body">
                {/* Visual components render ABOVE the text */}
                {msg.components?.map((comp) => (
                  <ComponentRenderer
                    key={comp.id}
                    component={comp}
                    onFollowUp={handleFollowUp}
                  />
                ))}

                {/* Text content */}
                {msg.content && (
                  <div
                    className={`message-text ${msg.isStreaming ? "streaming" : ""}`}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                  />
                )}

                {/* Queued indicator */}
                {msg.isQueued && (
                  <div className="queued-indicator">
                    <span className="tool-spinner" />
                    <span>Waiting for previous response...</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Tool indicators (during streaming) */}
          {toolIndicators.length > 0 && (
            <div className="tool-group">
              <div className="tool-group-summary">
                <span className="tool-group-icon">
                  {toolIndicators.every((t) => t.status === "done") ? (
                    <span className="tool-check">{"\u2713"}</span>
                  ) : (
                    <span className="tool-spinner" />
                  )}
                </span>
                <span>
                  {toolIndicators.length === 1
                    ? toolIndicators[0]!.status === "running"
                      ? `Running ${formatToolName(toolIndicators[0]!.name)}...`
                      : `Used 1 tool`
                    : toolIndicators.every((t) => t.status === "done")
                      ? `Used ${toolIndicators.length} tools`
                      : `Using ${toolIndicators.length} tools...`}
                </span>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="message assistant">
              <div className="avatar">HI</div>
              <div className="message-body">
                <div className="error-text">Error: {error}</div>
                {error.toLowerCase().includes("upgrade") && (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    style={{
                      marginTop: 8,
                      padding: "6px 16px",
                      borderRadius: 6,
                      border: "none",
                      background: "#3b82f6",
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    View Plans
                  </button>
                )}
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Guided chips */}
        {chips.length > 0 && (
          <div className="input-area" style={{ paddingBottom: 0 }}>
            <GuidedChips
              chips={chips}
              onChipClick={handleFollowUp}
              disabled={isStreaming}
            />
          </div>
        )}

        {/* Input area */}
        <div className="input-area">
          <div className="input-row">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder="Ask about a property..."
              rows={1}
            />
            <button
              className="send-btn"
              onClick={handleSend}
              disabled={!inputValue.trim()}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </div>
        </div>
      </main>

      {/* Deal Detail Panel */}
      {detailDealId && (
        <DealDetailPanel
          dealId={detailDealId}
          onClose={() => setDetailDealId(null)}
          onFollowUp={handleFollowUp}
          onOpenConversation={(convo) => {
            setDetailDealId(null);
            openConversation(convo);
          }}
          onUpdateDeal={(updated) => {
            updateDeal(updated.id, {
              status: updated.status as DealData["status"],
              title: updated.title,
              notes: updated.notes,
            });
          }}
        />
      )}

      {/* Settings Panel */}
      {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <PlanSelector
          currentTier="free"
          onClose={() => setShowUpgradeModal(false)}
        />
      )}
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────

function WelcomeScreen({
  onExample,
  activeDeals,
  onContinueDeal,
}: {
  onExample: (msg: string) => void;
  activeDeals: DealData[];
  onContinueDeal: (deal: DealData) => void;
}) {
  const examples = [
    {
      icon: "\uD83C\uDFD8",
      label: "Comparable sales near Carmel",
      msg: "Show me comparable sales within 1 mile of 376 Gradle Dr, Carmel",
    },
    {
      icon: "\uD83D\uDD0D",
      label: "Screen a site for development",
      msg: "Screen 1810 E 106th St Carmel for commercial use",
    },
    {
      icon: "\uD83D\uDCB0",
      label: "Tax impact of buying a rental",
      msg: "What happens if I buy 1810 E 106th St Carmel as a rental?",
    },
    {
      icon: "\uD83D\uDCCA",
      label: "Market snapshot for Fishers",
      msg: "What's the commercial real estate market like in Fishers?",
    },
  ];

  return (
    <div className="welcome">
      <div className="welcome-icon">
        <WelcomeBrandIcon />
      </div>
      <h2>Hoosier Intel</h2>
      <p>
        Indiana commercial real estate intelligence &mdash; tax analysis, site
        screening, and market data powered by statewide data.
      </p>

      {/* Continue a Deal section */}
      {activeDeals.length > 0 && (
        <>
          <div className="welcome-divider">
            <span className="welcome-divider-line" />
            <span className="welcome-divider-text">Continue a deal</span>
            <span className="welcome-divider-line" />
          </div>
          <div className="welcome-deals">
            {activeDeals.map((deal) => (
              <button
                key={deal.id}
                className="welcome-deal-btn"
                onClick={() => onContinueDeal(deal)}
              >
                <div className="welcome-deal-top">
                  <span className="welcome-deal-address">
                    {deal.address || deal.title || deal.parcel_id || "Untitled Deal"}
                  </span>
                  <span
                    className="welcome-deal-stage"
                    style={{
                      color: deal.status === "active" ? "var(--success)" : "var(--accent)",
                    }}
                  >
                    {deal.status}
                  </span>
                </div>
                <span className="welcome-deal-action">Continue Analysis →</span>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="welcome-divider">
        <span className="welcome-divider-line" />
        <span className="welcome-divider-text">Try asking</span>
        <span className="welcome-divider-line" />
      </div>
      <div className="examples-grid">
        {examples.map((ex) => (
          <button key={ex.label} className="example-btn" onClick={() => onExample(ex.msg)}>
            <span className="example-btn-icon">{ex.icon}</span>
            <span className="example-btn-text">{ex.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

